
import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, } from "recharts";
import { useRecoilState, useRecoilValue } from "recoil";
import { spinnerLiteState, spinnerState } from "../../states";
import { Heading, SpinnerLite, TypographyColor, TypographyType } from "../../ui-elements";
import { useSearchParams } from "react-router-dom";


interface lineChartProps {
  graphData: any,
  selectedData: string,
  selectedDataIndex: number,
  setSelectedData: (selectedData: string) => void,
  color: string
  headerText: string
  breakDownType: string
  selectedYear: string,
  setSelectedDataIndex: (selectedDataIndex: number) => void
}


export const LineChartComponent = ({ graphData, selectedData, selectedDataIndex, setSelectedDataIndex, setSelectedData, color, headerText, breakDownType, selectedYear }: lineChartProps) => {
  const spinner = useRecoilValue(spinnerState);
  const [spinnerLite, setSpinnerLite] = useRecoilState(spinnerLiteState);
  const [searchParams, setSearchParams] = useSearchParams();
  // Function to format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0"); // Ensure 2-digit day
    const month = date.toLocaleString("en-US", { month: "short" }); // Get short month name
    const year = date.getFullYear(); // Get full year

    return `${day}-${month}-${year}`;
  };


  // Transform graphData
  graphData.forEach((category: any) => {
    // Format dates in growthInTime
    category.growthInTime = category.growthInTime.map((entry: any) => ({
      ...entry,
      date: formatDate(entry.date),
    }));
  });

  const handleLineClick = (name: string) => {
    setSelectedData(name);
  }

  const [combinedData, setCombinedData] = useState<any[]>([]);

  useEffect(() => {
    // Prepare combined data for the chart whenever graphData changes
    const newCombinedData: any[] = [];
    graphData.forEach((category: any) => {
      category.growthInTime.forEach((entry: any) => {
        const existing = newCombinedData.find((d) => d.date === entry.date);
        if (existing) {
          existing[category.name] = entry.value;
          existing[`${category.name}_avg`] = entry.averageValue;
          existing[`${category.name}_desc`] = entry.description;
          existing[`${category.name}_date`] = entry.date;
        } else {
          newCombinedData.push({
            date: entry.date,
            [category.name]: entry.value,
            [`${category.name}_avg`]: entry.averageValue,
            [`${category.name}_desc`]: entry.description,
            [`${category.name}_date`]: entry.date,
          });
        }
      });
    });

    newCombinedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    // Update the state with the new combined data
    setCombinedData(newCombinedData);

  }, [graphData]);

  // Get unique years for X-axis
  const uniqueYears = Array.from(
    new Set(combinedData.map(item => new Date(item.date).getFullYear()))
  ).sort((a, b) => b - a); // Ensure years are sorted


  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      var data;
      if (payload.length > selectedDataIndex && selectedDataIndex !== -1) {
        data = payload[selectedDataIndex].payload;
      }
      else {
        data = payload[0].payload;
      }

      // Check if catchment is present in searchParams
            const catchmentId = searchParams.get('catchment');

      const avrgText = data[`${payload.length > selectedDataIndex && selectedDataIndex !== -1 ? payload[selectedDataIndex].name : payload[0].name}_desc`]
        ?.split("than")[1]        // get text after 'than'
        ?.trim()                  // remove extra spaces
        ?.replace(/^\w/, (c: string) => c.toUpperCase()) || "";

      return (
        
        <div
          style={{
            position: "relative",
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "10px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            fontSize: "14px",
            fontFamily: "Arial, sans-serif",
          }}
        >

          <div style={{ display: "flex", flexDirection: 'row', alignItems: 'center' }} className="p-1">
            {/* <MdOutlineDateRange className="me-2" /> */}
            <p style={{ margin: 0 }} className="tooltipLinechartDate">{data[`${payload.length > selectedDataIndex && selectedDataIndex !== -1 ? payload[selectedDataIndex].name : payload[0].name}_date`]}</p>
          </div>
          <hr style={{ margin: 0 }} />
          <div style={{ display: "flex", flexDirection: 'row', alignItems: 'center' }} className="p-1">
            <div style={{ height: '0.306vh', width: '0.926vw', backgroundColor: color, }} className="me-1"></div>

            <h6 style={{ margin: 0, color: "#000", }} className="me-1 margin-left-1 tooltipLinechartDate">{(payload.length > selectedDataIndex && selectedDataIndex !== -1) ? payload[selectedDataIndex].name : payload[0].name} PoI:</h6>
            <p style={{ margin: 0, fontWeight: "bold" }} className="tooltipLinechartDate">
              {(payload.length > selectedDataIndex && selectedDataIndex !== -1) ? payload[selectedDataIndex].value.toLocaleString() : payload[0].value.toLocaleString()}{" "}
            </p>

          </div>
          <p style={{ margin: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', fontSize: '0.5785vw', color: '#3C3C4399', fontWeight: '500', fontFamily: "Poppins" }} className="ms-1">
            {/* {data[`${payload.length > selectedDataIndex && selectedDataIndex !== -1 ? payload[selectedDataIndex].name : payload[0].name}_desc`].includes("lesser") ? <IoIosArrowDown style={{ color: "#CA1A0A" }} className="me-2" /> : <IoIosArrowUp style={{ color: "#0DA62E" }} className="me-1" />} */}
            {data[`${payload.length > selectedDataIndex && selectedDataIndex !== -1 ? payload[selectedDataIndex].name : payload[0].name}_desc`]}
          </p>
          <hr style={{ margin: 0 }} />
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <div style={{ height: '0.306vh', width: '0.926vw', backgroundColor: " #4D4D4D " }} className="me-1 margin-left-1"></div>
            <div style={{ height: '0.306vh', width: '0.926vw', backgroundColor: " #4D4D4D " }} className="me-1"></div>
            <p style={{ margin: 0, }} className="p-1 tooltipLinechartDate">
              {avrgText}
            </p>
            <p style={{ margin: 0, fontWeight: "bold" }} className="p-1 tooltipLinechartDate">
              : {data[`${(payload.length > selectedDataIndex && selectedDataIndex !== -1) ? payload[selectedDataIndex].name : payload[0].name}_avg`].toLocaleString()}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom legend with modified names and icons
  const CustomLegend = (props: any) => {
    const { payload } = props;
    let otherSubcategoryRendered = false; // Track if "Other Subcategory Pols" has been rendered

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {Array.isArray(payload) &&
          payload
            .filter((entry: any) => {
              // Filter logic to render "Other Subcategory Pols" only once
              if (
                entry.dataKey !== selectedData &&
                entry.dataKey !== 'Average' &&
                !otherSubcategoryRendered
              ) {
                otherSubcategoryRendered = true;
                return true; // Allow rendering for the first unmatched entry
              }
              return entry.dataKey.toString().trim() === selectedData.toString().trim() || entry.dataKey === 'Average';
            })
            .map((entry: any, index: number) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: '10px',
                }}
              >
                {/* Icon */}
                {entry.dataKey === 'Average' ? (
                  // Two small horizontal lines for "Average" icon
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginRight: '5px',
                    }}

                  >
                    <div
                      style={{
                        width: '10px',
                        height: '3px',
                        backgroundColor: '#787486',
                      }}
                      className="me-1"
                    ></div>
                    <div
                      style={{
                        width: '10px',
                        height: '3px',
                        backgroundColor: '#787486',
                      }}
                    ></div>
                  </div>
                ) : (
                  <span
                    style={{
                      display: 'inline-block',
                      width: '0.66vw',
                      height: '0.51vh',
                      backgroundColor: entry.dataKey.toString().trim() === selectedData.toString().trim() ? color : entry.dataKey === 'Average' ? '#787486' : '#DBDBDB',
                      marginRight: '0.33vw',
                      borderRadius: "none",
                    }}
                  ></span>
                )}
                {/* Label */}
                {/* <span>
                  {entry.dataKey.toString().trim() === selectedData.toString().trim() ? selectedData
                    : entry.dataKey === 'Average' && headerText === 'District' ? 'State average'
                      : entry.dataKey === 'Average' && headerText === 'Sub District' ? 'State average' : entry.dataKey === 'Average' && headerText === 'Pin Code' ? "District average" : `Other ${headerText} Pols`}
                </span> */}
                <Heading
                  title={entry.dataKey.toString().trim() === selectedData.toString().trim() ? selectedData
                    : entry.dataKey === 'Average' && headerText === 'District' ? 'State average'
                      : entry.dataKey === 'Average' && headerText === 'Sub District' ? 'District Average' : entry.dataKey === 'Average' && headerText === 'Pin Code' ? "District Average" : `Other ${headerText} Pols`}
                  type={TypographyType.h5}
                  colour={TypographyColor.secondary}
                  classname='text-start'
                />
              </div>
            ))}
      </div>
    );
  };


  useEffect(() => {
    // Update the selectedDataIndex based on selectedData change
    const index = graphData.filter((item: any) => item.name !== 'Average').findIndex(
      (category: any) => category.geoId !== 0 && category.name === selectedData
    );

    if (index !== -1) {
      setSelectedDataIndex(index);
    } else if (graphData[0]?.geoId === 0) {
      setSelectedDataIndex(1); // Default to the second index
    } else {
      setSelectedDataIndex(0); // Default to the first index
    }
  }, [selectedData, graphData]);





  // // Formatter function for Y-axis ticks
  // const formatYAxis = (value: number): string => {
  //   return `${(value / 1000).toFixed(0)}L`; // Convert to 'L' format
  // };

  const interval = combinedData.length > 0 ? Math.floor(combinedData.length / 10) : 1; // Set a minimum interval of 1 for small arrays

  useEffect(() => {
    setSpinnerLite(prev => prev + 1);
  }, [graphData]);

  useEffect(() => {
    if (combinedData.length > 0) {
      setSpinnerLite(prev => prev - 1); // Prevent going below 0
    }
  }, [combinedData]);

  const years = ["2015-Jan-01", "2014-Jan-01", "2016-Jan-01", "2017-Jan-01", "2018-Jan-01", "2019-Jan-01", "2020-Jan-01", "2021-Jan-01", "2022-Jan-01", "2023-Jan-01", "2024-Jan-01"];



  return (
    (spinner.spinners.length === 0 && spinnerLite > 0 && combinedData.length === 0 ?
      <div className='w-100' style={{ height: '100%' }}>
        <SpinnerLite />
      </div> :
      <ResponsiveContainer width="100%" height={350} >
        <LineChart data={combinedData} >
          <CartesianGrid horizontal={false} vertical={false} />
          {selectedYear === '1' ? <XAxis
            dataKey="date"
            fontSize='0.81vw'
            style={{ fontWeight: 600, }}
            tickLine={false}
            axisLine={false}
            // padding={{ left: 20, right: 50 }}
            tickMargin={5}
            interval={interval}
          /> : <XAxis
            dataKey="date"
            fontSize='0.81vw'
            style={{ fontWeight: 600, }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value: string) => new Date(value).getFullYear().toString()}
            tickMargin={5}
            // padding={{ left: 20, right: 20 }}
            ticks={uniqueYears.map((year) => {
              // Find the first instance of each year in the dataset
              const match = combinedData.find(item => new Date(item.date).getFullYear() === year);
              return match?.date || year.toString(); // Use `date` value if available
            })}
            minTickGap={20}
          />
          }

          <YAxis tickLine={false} // Optionally hide tick lines too
            axisLine={false} // Hide the X-axis line
            // tickFormatter={formatYAxis}
            // tickMargin={10}
            fontSize='0.81vw'
            style={{ fontWeight: 600, }}
            padding={{ top: 10, bottom: 10 }}

          />

          <Tooltip content={CustomTooltip} cursor={true} />

          <Legend content={<CustomLegend />} wrapperStyle={{ paddingTop: '1rem' }} iconType={'square'} />
          {graphData
            .filter((category: any) => category.name !== "Average").map((category: any, index: number) => (
              <Line
                key={category.name}
                type="monotone"
                dataKey={category.name}
                stroke={category.name.toString().trim() === selectedData.toString().trim() ? color : "#DBDBDB"}
                strokeWidth={category.name.toString().trim() === selectedData.toString().trim() ? 5 : 1}
                connectNulls={true}
                onClick={() => handleLineClick(category.name)}
                style={{ cursor: 'pointer' }}
                dot={(props) => {
                  // Show dot only on hover
                  return (
                    <circle
                      key={`circlelocation1-${props.cx}`}
                      cx={props.cx}
                      cy={props.cy}
                      r={6}
                      fill={color}
                      style={{
                        visibility: props.isHovered && category.name === selectedData ? 'visible' : 'hidden',
                      }}
                    />
                  );
                }}
              />
            ))}
          {(breakDownType === 'Sub District' || breakDownType === 'Pin Code') && graphData
            .filter((category: any) => category.name === "Average") // Use `filter` for array traversal
            .map((category: any, index: number) => (
              <Line
                key={category.name}
                type="monotone"
                dataKey={category.name}
                stroke="#787486"
                strokeWidth={2}
                strokeDasharray="5 5" // Creates a dashed line
                connectNulls={true}

                dot={(props) => {
                  // Show dot only on hover
                  return (
                    <circle
                      key={`circlelocation2-${props.cx}`}
                      cx={props.cx}
                      cy={props.cy}
                      r={6}
                      fill={color}
                      style={{
                        visibility: props.isHovered ? 'visible' : 'hidden',
                      }}
                    />
                  );
                }}
              />
            ))}
        </LineChart>
      </ResponsiveContainer>
    )
  );
};
